import React, { useState, useEffect } from "react";
import axios from "axios";
import BASE_URL from "../config";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader"; // <-- Import Loader

const CreateTeam = () => {
  const navigate = useNavigate(); // ✅ Initialize useNavigate
  const [formData, setFormData] = useState({
    name: "",
    department: "",
    deadline: "",
    start_date: "", // Add start_date
    lead: "",
    members: [],
    client: "",
  });

  const [teamOptions, setTeamOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]); // <-- Add state for projects
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        // Fetch users
        const usersRes = await axios.get(`${BASE_URL}api/user/active-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userOptions = usersRes.data.map((user) => {
          let roleLabel = "";
          switch (user.role) {
            case "user":
              roleLabel = "Software Engineer";
              break;
            case "admin":
              roleLabel = "CTO-Admin";
              break;
            case "tester":
              roleLabel = "Tester";
              break;
            default:
              roleLabel = user.role;
          }

          return {
            value: user.id,
            text: `${user.username} - ${roleLabel}`,
            key: user.id,
          };
        });

        setTeamOptions(userOptions);

        // Fetch clients
        const clientsRes = await axios.get(`${BASE_URL}api/clients/active-clients`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const clientOpts = clientsRes.data.map((client) => ({
          value: client.id,
          text: client.name,
          key: client.id,
        }));

        setClientOptions(clientOpts);

        // Fetch projects for dropdown
        const projectsRes = await axios.get(`${BASE_URL}api/project/find-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProjectOptions(projectsRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false); // Only hide loader after projects are fetched
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, selectedOptions } = e.target;
    if (type === "select-multiple") {
      const selectedValues = Array.from(selectedOptions).map((option) => option.value);
      setFormData({
        ...formData,
        [name]: selectedValues,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const { name, department, deadline, start_date, lead, members, client } = formData;
    let formErrors = {};
    let isValid = true;

    if (!name) {
      formErrors.name = "Team name is required";
      isValid = false;
    }
    if (!department) {
      formErrors.department = "Department is required";
      isValid = false;
    }
    if (!start_date) {
      formErrors.start_date = "Project start date is required";
      isValid = false;
    }
    if (!deadline) {
      formErrors.deadline = "Deadline is required";
      isValid = false;
    }
    if (!lead) {
      formErrors.lead = "Team lead is required";
      isValid = false;
    }
    if (members.length === 0) {
      formErrors.members = "At least one member is required";
      isValid = false;
    }
    if (!client) {
      formErrors.client = "Client is required";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const { name, department, deadline, start_date, lead, members, client } = formData;

    const payload = {
      name,
      department,
      deadline,
      start_date, // Send start_date to API
      lead: Number(lead),
      client: Number(client),
      members: members.join(",")
    };

    console.log("CreateTeam payload:", payload); // <-- Log the payload before sending

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(`${BASE_URL}api/team/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok) {
        toast.success(result.message || "Team created successfully!"); // ✅ Use toast for success
        navigate("/teams"); // ✅ Redirect to teams page
      } else {
        toast.error(result.message || "Failed to create team."); // ✅ Use toast for error
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error creating team. Please try again later."); // ✅ Use toast for error
    }
  };

  // Helper to get today's date in YYYY-MM-DD format
  const todayStr = new Date().toISOString().split("T")[0];

  if (loading || projectOptions.length === 0) {
    // Show loader until projects are fetched and dropdown is populated
    return <Loader message="Loading..." />;
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Create New Team</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="team-name"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("team-name")?.focus()}
          >
            Team Name
          </label>
          <input
            id="team-name"
            type="text"
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <p className="text-danger">{errors.name}</p>}
        </div>

        {/* New: Project Start Date */}
        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="start-date"
            style={{ cursor: "pointer" }}
            onMouseDown={e => {
              e.preventDefault();
              const input = document.getElementById("start-date");
              if (input) input.showPicker ? input.showPicker() : input.focus();
            }}
          >
            Project Start Date
          </label>
          <input
            id="start-date"
            type="date"
            className="form-control"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            min={todayStr}
          />
          {errors.start_date && <p className="text-danger">{errors.start_date}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="deadline"
            style={{ cursor: "pointer" }}
            onMouseDown={e => {
              e.preventDefault();
              const input = document.getElementById("deadline");
              if (input) input.showPicker ? input.showPicker() : input.focus();
            }}
          >
            Deadline
          </label>
          <input
            id="deadline"
            type="date"
            className="form-control"
            name="deadline"
            value={formData.deadline}
            onChange={handleChange}
            min={todayStr}
          />
          {errors.deadline && <p className="text-danger">{errors.deadline}</p>}
        </div>
        
        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="department"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("department")?.focus()}
          >
            Project
          </label>
          <select
            id="department"
            className="form-select"
            name="department"
            value={formData.department}
            onChange={handleChange}
          >
            <option value="">Select Project</option>
            {projectOptions.map((project) => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
          {errors.department && <p className="text-danger">{errors.department}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="lead"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("lead")?.focus()}
          >
            Team Lead
          </label>
          <select
            id="lead"
            className="form-select"
            name="lead"
            value={formData.lead}
            onChange={handleChange}
          >
            <option value="">Select Team Lead</option>
            {teamOptions.map((option) => (
              <option key={option.key} value={option.value}>
                {option.text}
              </option>
            ))}
          </select>
          {errors.lead && <p className="text-danger">{errors.lead}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="members"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("members")?.focus()}
          >
            Members
          </label>
          <select
            id="members"
            className="form-select"
            name="members"
            multiple
            value={formData.members}
            onChange={handleChange}
          >
            {teamOptions
              .filter((option) => !formData.lead || option.value !== Number(formData.lead))
              .map((option) => (
                <option key={option.key} value={option.value}>
                  {option.text}
                </option>
              ))}
          </select>
          {errors.members && <p className="text-danger">{errors.members}</p>}
        </div>

        <div className="mb-3">
          <label
            className="form-label"
            htmlFor="client"
            style={{ cursor: "pointer" }}
            onClick={() => document.getElementById("client")?.focus()}
          >
            Client
          </label>
          <select
            id="client"
            className="form-select"
            name="client"
            value={formData.client}
            onChange={handleChange}
          >
            <option value="">Select Client</option>
            {clientOptions.map((client) => (
              <option key={client.key} value={client.value}>
                {client.text}
              </option>
            ))}
          </select>
          {errors.client && <p className="text-danger">{errors.client}</p>}
        </div>

        <button type="submit" className="btn btn-primary">
          Create Team
        </button>
      </form>
    </div>
  );
};

export default CreateTeam;
